import type { Entry } from "../types";

export const administrationMessages = {
  enable: { ko: "활성화", en: "Enable" },
  disable: { ko: "비활성화", en: "Disable" },
  rowActions: { ko: "작업", en: "Actions" },
  edit: { ko: "수정", en: "Edit" },
  delete: { ko: "삭제", en: "Delete" },
  confirmArchiveOrganization: {
    ko: "{name} 조직을 삭제하시겠습니까? 접근은 중단되지만 기존 기록은 보존됩니다.",
    en: "Delete {name}? Access will be disabled, but existing records will be preserved.",
  },
  aiReviewBatchTitle: { ko: "AI 심사 분할", en: "AI review batching" },
  aiReviewBatchSize: { ko: "호출당 근거 수", en: "References per call" },
  aiReviewBatchConcurrency: { ko: "동시 호출 수", en: "Calls at once" },
  aiReviewBatchHelp: {
    ko: "AI는 전달받은 근거마다 판정을 반환하므로, 호출당 근거 수가 응답 길이를 결정합니다. 적용 근거가 이 값 이하이면 기존과 같이 한 번만 호출합니다. 동시 호출 수를 올리면 대기 시간은 줄지만 VaultSage 부하가 함께 늘어납니다.",
    en: "The AI returns a verdict for every reference it receives, so references per call sets the response length. A source set at or below this value is reviewed in a single call, as before. Raising calls at once shortens the wait but increases VaultSage load in step.",
  },
  aiReviewBatchSummary: {
    ko: "AI 심사 {size}건/호출 ,  동시 {concurrency}회",
    en: "AI review {size}/call ,  {concurrency} at once",
  },
  deleteMemberConfirm: {
    ko: "{email} 구성원을 삭제하시겠습니까? 조직 접근 권한이 즉시 해제되고, 진행 중인 검토 할당은 해제됩니다. 이 작업은 되돌릴 수 없습니다.",
    en: "Delete the member {email}? Their access ends immediately and their open review assignments are released. This action cannot be undone.",
  },
  deleteMembershipConfirm: {
    ko: "{email} 사용자를 이 조직에서 제외하시겠습니까? 이 작업은 되돌릴 수 없습니다.",
    en: "Remove {email} from this organization? This action cannot be undone.",
  },
  cancelInviteConfirm: {
    ko: "{email}의 초대를 취소하시겠습니까?",
    en: "Cancel the invitation for {email}?",
  },
  view: { ko: "보기", en: "View" },
  adminUsersSearchPlaceholder: { ko: "이메일 또는 이름으로 검색", en: "Search by email or name" },
  pendingInvitations: { ko: "대기 중인 초대", en: "Pending Invitations" },
  pendingInvitationsHelp: {
    ko: "아직 로그인하지 않아 계정이 생성되지 않은 초대입니다. 첫 로그인 시 자동으로 활성화됩니다.",
    en: "Invited emails with no account yet — they activate automatically on first login.",
  },
  invitedBy: { ko: "초대한 사람", en: "Invited by" },
  invitedAt: { ko: "초대 일시", en: "Invited" },
  cancelInvite: { ko: "초대 취소", en: "Cancel invite" },
  add: { ko: "추가", en: "Add" },
  lastLogin: { ko: "최근 로그인", en: "Last login" },
  never: { ko: "없음", en: "Never" },
  platformRole: { ko: "플랫폼 권한", en: "Platform role" },
  addToOrganization: { ko: "조직에 배정", en: "Add to organization" },
  addToOrganizationHelp: {
    ko: "이메일과 조직, 역할을 지정하면 해당 사용자가 그 조직의 구성원으로 배정됩니다. 아직 가입하지 않은 이메일도 초대할 수 있습니다.",
    en: "Assign the given email to an organization with a role. The email doesn't need to belong to an existing account — it can also be a new invite.",
  },
  promoteSuperAdmin: { ko: "슈퍼 관리자로 지정", en: "Promote to Super Admin" },
  revokeSuperAdmin: { ko: "슈퍼 관리자 해제", en: "Revoke Super Admin" },
  confirmPromoteSuperAdmin: {
    ko: "이 사용자를 슈퍼 관리자로 지정하시겠습니까? 플랫폼의 모든 조직에 접근할 수 있게 됩니다.",
    en: "Promote this user to Super Admin? They will gain access to every organization on the platform.",
  },
  confirmRevokeSuperAdmin: {
    ko: "이 사용자의 슈퍼 관리자 권한을 해제하시겠습니까?",
    en: "Revoke this user's Super Admin access?",
  },
  cannotRemoveOwnSuperAdmin: {
    ko: "본인의 슈퍼 관리자 권한은 스스로 해제할 수 없습니다.",
    en: "You cannot remove your own Super Admin access.",
  },
  deleteRuleConfirm: {
    ko: "‘{title}’ 규칙을 삭제하시겠습니까? 모든 버전과 참고 이미지가 영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.",
    en: "Delete the rule “{title}”? All versions and reference images will be permanently deleted. This action cannot be undone.",
  },
  deleteReviewConfirm: {
    ko: "‘{title}’ 검토 요청을 삭제하시겠습니까? 모든 버전, 분석 결과 및 업로드 파일이 영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.",
    en: "Delete the review “{title}”? All revisions, analysis results, and uploaded files will be permanently deleted. This action cannot be undone.",
  },
  // Audit table columns
  auditTime: { ko: "시간", en: "Time" },
  auditAction: { ko: "동작", en: "Action" },
  auditActor: { ko: "수행자", en: "Actor" },
  auditTarget: { ko: "대상", en: "Target" },
  // Validation
  titleRequired: { ko: "제목을 입력하세요.", en: "Please enter a title." },
  bodyOrFileRequired: {
    ko: "광고 본문 또는 파일이 필요합니다.",
    en: "Advertising copy or a file is required.",
  },
  // Error fallbacks
  errSubmit: { ko: "제출에 실패했습니다.", en: "Submission failed." },
  errDecision: { ko: "결정 기록에 실패했습니다.", en: "Failed to record the decision." },
  errResubmit: { ko: "재제출에 실패했습니다.", en: "Resubmission failed." },
  errActivate: { ko: "활성화에 실패했습니다.", en: "Activation failed." },
  errCreate: { ko: "생성에 실패했습니다.", en: "Creation failed." },
  errInvite: { ko: "초대에 실패했습니다.", en: "Invitation failed." },
  errUpdate: { ko: "변경에 실패했습니다.", en: "Update failed." },
  errDelete: { ko: "삭제에 실패했습니다.", en: "Deletion failed." },
  errCreateOrganization: { ko: "조직 생성에 실패했습니다.", en: "Failed to create the organization." },
  errSelectOrganization: { ko: "조직 선택에 실패했습니다.", en: "Failed to select the organization." },
  profileTitle: { ko: "사용자 프로필", en: "User Profile" },
  profileIntro: {
    ko: "계정 정보와 서비스 권한을 확인하고 사용자 이름을 수정합니다.",
    en: "View your account details, access level, and update your user name.",
  },
  userName: { ko: "사용자 이름", en: "User Name" },
  name: { ko: "이름", en: "Name" },
  fullNamePlaceholder: { ko: "홍길동", en: "Jane Doe" },
  department: { ko: "부서 / 팀", en: "Department / Team" },
  departmentPlaceholder: { ko: "예: 마케팅팀", en: "For example, Marketing" },
  organization: { ko: "소속 조직", en: "Organization" },
  userAccessLevel: { ko: "사용자 권한", en: "User Access Level" },
  editName: { ko: "이름 수정", en: "Edit Name" },
  nameUpdated: { ko: "사용자 이름이 변경되었습니다.", en: "User name updated successfully." },
  errUpdateProfile: { ko: "프로필 업데이트에 실패했습니다.", en: "Failed to update profile." },
  navQueueHealth: { ko: "분석 처리 현황", en: "Analysis Queue" },
  queueHealthHelp: {
    ko: "제출된 검토는 순서대로 처리되는 대기열에 저장됩니다. 처리량과 지연, 실패한 작업을 확인할 수 있습니다.",
    en: "Submitted reviews are stored in a durable queue and analysed in order. This page shows throughput, delay, and jobs that need attention.",
  },
  queueWaiting: { ko: "대기 중", en: "Waiting" },
  queueRunning: { ko: "분석 중", en: "Analysing" },
  queueRetryingCount: { ko: "재시도 {value}건 포함", en: "includes {value} retrying" },
  queueConcurrency: {
    ko: "인스턴스당 동시 처리 {value}건",
    en: "{value} at a time per instance",
  },
  queueOldestWait: { ko: "최장 대기 시간", en: "Longest wait" },
  queueEstimatedWait: { ko: "예상 소요 {value}", en: "about {value} to clear" },
  queueTurnaround: { ko: "평균 처리 시간", en: "Average turnaround" },
  queueCompletedLastHour: { ko: "최근 1시간 {value}건 완료", en: "{value} completed in the last hour" },
  queueSeconds: { ko: "{value}초", en: "{value}s" },
  queueMinutes: { ko: "{value}분", en: "{value}m" },
  queueGeneratedAt: { ko: "기준 시각 {value}", en: "as of {value}" },
  queueIssuesTitle: { ko: "확인이 필요한 작업", en: "Jobs needing attention" },
  queueIssuesHelp: {
    ko: "재시도를 모두 소진한 작업과 처리 중 중단된 작업입니다. 실패한 검토는 요청자가 다시 제출할 수 있습니다.",
    en: "Jobs that exhausted their retries or stalled mid-analysis. A failed review can be resubmitted by its requester.",
  },
  queueNoIssues: { ko: "확인이 필요한 작업이 없습니다.", en: "No jobs need attention." },
  queueIssueFailed: { ko: "재시도 소진 후 실패", en: "Failed after retries" },
  queueIssueLeaseExpired: { ko: "처리 중 중단", en: "Stalled mid-analysis" },
  queueIssueRetryExhausting: { ko: "재시도 한계 임박", en: "Nearly out of retries" },
  queueColReview: { ko: "검토", en: "Review" },
  queueColProblem: { ko: "상태", en: "Problem" },
  queueColAttempts: { ko: "시도", en: "Attempts" },
  queueColWaiting: { ko: "경과 시간", en: "Elapsed" },
  queueColUpdated: { ko: "최근 변경", en: "Updated" },

  // Approval page appended to approved documents
  approvalPageTitle: { ko: "승인 페이지 서명", en: "Approval page signature" },
  approvalPageHelp: {
    ko: "승인된 광고물은 원본 파일 마지막에 승인 페이지가 추가된 형태로 다운로드됩니다. 그 페이지에 인쇄될 서명 문구를 지정하세요.",
    en: "An approved creative is downloaded as the original file with an approval page appended. This is the signature text printed on that page.",
  },
  approvalTemplateLabel: { ko: "서명 문구", en: "Signature text" },
  approvalTemplateHelp: {
    ko: "아래 항목을 클릭하면 커서 위치에 삽입됩니다. 승인 시점의 문구가 그대로 보관되므로, 이미 발급된 문서는 이후 수정에 영향받지 않습니다.",
    en: "Click a field below to insert it at the cursor. The wording is frozen when a review is approved, so documents already issued are unaffected by later edits.",
  },
  approvalPlaceholders: { ko: "삽입 가능한 항목", en: "Available fields" },
  approvalPreview: { ko: "미리보기", en: "Preview" },
  approvalResetDefault: { ko: "기본 문구로 되돌리기", en: "Reset to default" },
  approvalTemplateEmpty: { ko: "서명 문구를 입력하세요.", en: "Enter the signature text." },
  approvalTemplateUnknownPlaceholder: {
    ko: "사용할 수 없는 항목이 있습니다: {names}",
    en: "Unsupported field(s): {names}",
  },
  approvalSealTitle: { ko: "직인 이미지 (선택)", en: "Seal image (optional)" },
  approvalSealHelp: {
    ko: "승인 페이지 오른쪽에 표시됩니다. PNG 또는 JPEG, 5MB 이하.",
    en: "Drawn on the right of the approval page. PNG or JPEG, up to 5 MB.",
  },
  approvalSealTooLarge: {
    ko: "직인 이미지는 5MB 이하여야 합니다.",
    en: "The seal image must be 5 MB or smaller.",
  },
  approvalSealUpload: { ko: "직인 업로드", en: "Upload seal" },
  approvalSealReplace: { ko: "직인 변경", en: "Replace seal" },
  approvalSealRemove: { ko: "직인 삭제", en: "Remove seal" },
  approvalSealNone: { ko: "등록된 직인이 없습니다.", en: "No seal is configured." },

  // Placeholder field labels, shown on the insert buttons
  approvalFieldOrganization: { ko: "조직명", en: "Organization" },
  approvalFieldReviewId: { ko: "확인필 번호", en: "Review ID" },
  approvalFieldReviewTitle: { ko: "심의 제목", en: "Review title" },
  approvalFieldProductName: { ko: "상품명", en: "Product" },
  approvalFieldChannel: { ko: "채널", en: "Channel" },
  approvalFieldDecision: { ko: "심의 결과", en: "Decision" },
  approvalFieldReviewer: { ko: "심의자", en: "Reviewer" },
  approvalFieldReviewerEmail: { ko: "심의자 이메일", en: "Reviewer email" },
  approvalFieldApprovedAt: { ko: "승인 일시", en: "Approved at" },

  // Platform LLM sampling parameters (Super Admin)
  llmSettingsIntro: {
    ko: "VaultSage 채팅 호출에 전달되는 샘플링 파라미터입니다. 기본값은 동일한 입력에 동일한 결과가 나오도록 설정되어 있습니다. 모든 조직의 광고 검토, Ask Sage, Educate Sage에 적용됩니다.",
    en: "Sampling parameters forwarded on every VaultSage chat call. Defaults keep identical inputs producing identical answers. Applies to material review, Ask Sage, and Educate Sage for every organization.",
  },
  llmSettingsHelp: {
    ko: "Temperature를 높이면 응답이 다양해지고, 같은 입력에서도 결과가 달라질 수 있습니다. top_p와 seed도 재현성에 영향을 줍니다.",
    en: "Raising temperature makes answers more varied, so the same input may produce different results. top_p and seed also affect reproducibility.",
  },
  llmTemperature: { ko: "Temperature", en: "Temperature" },
  llmTemperatureHelp: {
    ko: "0(결정론적)부터 2(최대 무작위성)까지. 기본값 0.",
    en: "From 0 (deterministic) to 2 (maximum randomness). Default 0.",
  },
  llmTopP: { ko: "Top P", en: "Top P" },
  llmTopPHelp: {
    ko: "핵 샘플링 범위(0–1). 기본값 1.",
    en: "Nucleus sampling range (0–1). Default 1.",
  },
  llmSeed: { ko: "Seed", en: "Seed" },
  llmSeedHelp: {
    ko: "제공자 시드(0 이상 정수). 기본값 0.",
    en: "Provider seed (non-negative integer). Default 0.",
  },
  llmTemperatureInvalid: {
    ko: "Temperature는 0 이상 2 이하여야 합니다.",
    en: "Temperature must be between 0 and 2.",
  },
  llmTopPInvalid: {
    ko: "Top P는 0 이상 1 이하여야 합니다.",
    en: "Top P must be between 0 and 1.",
  },
  llmSeedInvalid: {
    ko: "Seed는 0 이상의 정수여야 합니다.",
    en: "Seed must be a non-negative integer.",
  },
  llmRestoreDefaults: {
    ko: "결정론적 기본값으로 복원",
    en: "Restore deterministic defaults",
  },
  llmSettingsCardHelp: {
    ko: "채팅 응답의 무작위성(temperature, top_p, seed)을 설정합니다.",
    en: "Configure chat randomness (temperature, top_p, seed).",
  },
  llmSettingsUpdatedAt: { ko: "마지막 변경", en: "Last updated" },

  // Channels and the 구분1/구분2 taxonomy
} as const satisfies Record<string, Entry>;
